/* ===================================================================
   Onboarding -- the UNIFIED derived-completion checklist home (Phase 3,
   2026-07-07, BUILD-SPEC §4 + §13.2). This REPLACES the old linear 7-step
   wizard shell AND the separate /first-run checklist. The checklist IS
   onboarding and the post-signup home.

   HOW IT WORKS NOW:
     - Default view = the checklist home (<OnboardingChecklist variant="full">),
       whose completion is DERIVED FROM REAL ACCOUNT DATA (useSetupProgress),
       never from "clicked Next".
     - Tapping an item LAUNCHES that item's existing setup FLOW as a task
       (activeTask), then returns to the checklist. The old wizard step flows
       are preserved verbatim as those tasks:
         industry   -> business category (tailors copy; future Item-0 slot)
         connect    -> Google connect + ConfirmBusinessCard (Phase-1 canonical
                       status + wrong-listing gate -- UNCHANGED)
         customers  -> connectors / Zapier / CSV / add-manually / suggest
         reactivation -> ReactivationStep (consent-gated; UNCHANGED)
         voice      -> brand-voice tone/sign-off/mention + live preview,
                       persisted to /business
         widget     -> per-tenant embed snippet copy
         firstask   -> navigates to the single Requests page behind the
                       GREEN-LIGHT SEND GATE (canonical connected + >=1
                       customer). The composer + card-at-send step live there.
     - There is NO forced linear progression and NO separate "done" screen; the
       home's derived state IS the completion surface.

   PRESERVED: the Phase-1 canonical-status reads, the ConfirmBusinessCard, the
   green-light send gate, "nothing auto-posts", per-tenant OAuth-resume, and the
   DEV/subscription gates upstream. Only WORKING paths are surfaced.

   Persistence + the reactivation marker live in lib/onboardingState (shared
   with the embedded dashboard checklist). clearOnboardingState +
   onboardingInProgress are RE-EXPORTED here so existing importers (LogoutButton,
   Settings, Integrations) keep working unchanged.
   =================================================================== */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import GoogleG from '../components/GoogleG';
import VendorLogo from '../components/VendorLogo';
import type { VendorId } from '../components/VendorLogo';
import {
  useIntegrationRequest,
  useMe,
  useCustomerMutations,
  useConnectors,
  useCustomers,
  useUpdateBusiness,
  useWidget,
  useSetupProgress,
} from '../hooks';
import { AddCustomerModal } from '../components/AddCustomerModal';
import { ConfirmBusinessCard } from '../components/ConfirmBusinessCard';
import { ReactivationStep } from '../components/ReactivationStep';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { ClientPhotoUpload } from '../components/ClientPhotoUpload';
import { TriggerArmConfirm } from '../components/TriggerArmConfirm';
import { useBusiness } from '../hooks';
import { parseCustomersCsv } from '../lib/csv';
import { startGoogleConnect, API_BASE } from '../lib/api';
import { track, trackMilestoneOnce, getFirstSeen } from '../lib/analytics';
import { useSession } from '../auth/useSession';
import {
  loadOnboarding,
  saveOnboarding,
  clearOnboardingForTenant,
  setReactivationEnrolled,
  type OnboardingTaskId,
} from '../lib/onboardingState';
import '../styles/onboarding.css';

/* Re-export the shared persistence helpers so existing importers keep working
   (LogoutButton + Settings clear all onboarding state on logout; Integrations
   resumes the connect task after the Google OAuth round-trip). */
export {
  clearOnboardingState,
  onboardingInProgress,
} from '../lib/onboardingState';

/* Legacy ?step=N (used by older links / the Integrations OAuth return before
   Phase 3) maps onto the task ids so a deep link still lands correctly. */
const LEGACY_STEP_TO_TASK: Record<number, OnboardingTaskId> = {
  0: 'industry',
  1: 'connect',
  2: 'customers',
  3: 'reactivation',
  4: 'voice',
  5: 'widget',
  6: 'firstask',
};

const VALID_TASKS: OnboardingTaskId[] = [
  'diagnostic',
  'industry',
  'connect',
  'customers',
  'reactivation',
  'voice',
  'widget',
  'firstask',
];

/* ---- business category (no category is gated; tailors request/reply copy
   tone only). The full canonical list from ownthebox.net. ---- */
type BusinessCategory = { id: string; label: string };
const BUSINESS_CATEGORIES: BusinessCategory[] = [
  { id: 'accountant_bookkeeping', label: 'Accountant / Bookkeeping' },
  { id: 'appliance_repair', label: 'Appliance Repair' },
  { id: 'attorney_estate_planning', label: 'Attorney (Estate Planning)' },
  { id: 'attorney_general_practice', label: 'Attorney (General Practice)' },
  { id: 'auto_body_collision', label: 'Auto Body / Collision' },
  { id: 'auto_detailing', label: 'Auto Detailing' },
  { id: 'auto_repair', label: 'Auto Repair' },
  { id: 'bakery', label: 'Bakery' },
  { id: 'basement_finishing', label: 'Basement Finishing' },
  { id: 'basement_waterproofing', label: 'Basement Waterproofing' },
  { id: 'boat_service_storage', label: 'Boat Service / Storage' },
  { id: 'breakfast_lunch', label: 'Breakfast & Lunch' },
  { id: 'bulkhead_installation', label: 'Bulkhead Installation' },
  { id: 'cabinets_countertops', label: 'Cabinets / Countertops' },
  { id: 'cafe', label: 'Cafe' },
  { id: 'carpet_upholstery_cleaning', label: 'Carpet & Upholstery Cleaning' },
  { id: 'catering', label: 'Catering' },
  { id: 'custom_closets', label: 'Custom Closets' },
  { id: 'deck_building', label: 'Deck Building' },
  { id: 'dog_walking_boarding', label: 'Dog Walking / Boarding' },
  { id: 'door_installation', label: 'Door Installation' },
  { id: 'downsizing_services', label: 'Downsizing Services' },
  { id: 'driveway_paving', label: 'Driveway & Paving' },
  { id: 'drywall_plaster', label: 'Drywall / Plaster' },
  { id: 'electrical', label: 'Electrical' },
  { id: 'estate_sale_services', label: 'Estate Sale Services' },
  { id: 'farm_stand_specialty_grocer', label: 'Farm Stand / Specialty Grocer' },
  { id: 'fence_installation', label: 'Fence Installation' },
  { id: 'financial_planner', label: 'Financial Planner' },
  { id: 'fire_pit_outdoor_kitchen', label: 'Fire Pit / Outdoor Kitchen' },
  { id: 'flooring_hardwood_tile_carpet', label: 'Flooring (Hardwood / Tile / Carpet)' },
  { id: 'florist', label: 'Florist' },
  { id: 'furniture_store', label: 'Furniture Store' },
  { id: 'garage_door_service', label: 'Garage Door Service' },
  { id: 'garden_center_nursery', label: 'Garden Center / Nursery' },
  { id: 'general_contractor_additions', label: 'General Contractor / Additions' },
  { id: 'gutter_cleaning_guards', label: 'Gutter Cleaning / Guards' },
  { id: 'handyman', label: 'Handyman' },
  { id: 'hardscaping_patios', label: 'Hardscaping & Patios' },
  { id: 'holiday_lighting', label: 'Holiday Lighting' },
  { id: 'home_appraiser', label: 'Home Appraiser' },
  { id: 'home_inspection', label: 'Home Inspection' },
  { id: 'home_security_smart_home_av', label: 'Home Security / Smart Home / AV' },
  { id: 'hot_tub_spa', label: 'Hot Tub / Spa' },
  { id: 'house_cleaning_maid_service', label: 'House Cleaning / Maid Service' },
  { id: 'hvac_heating_cooling', label: 'HVAC / Heating & Cooling' },
  { id: 'insulation', label: 'Insulation' },
  { id: 'insurance_agent', label: 'Insurance Agent' },
  { id: 'interior_design', label: 'Interior Design' },
  { id: 'irrigation_sprinklers', label: 'Irrigation & Sprinklers' },
  { id: 'junk_removal_hauling', label: 'Junk Removal / Hauling' },
  { id: 'kitchen_remodeling', label: 'Kitchen Remodeling' },
  { id: 'landscape_design', label: 'Landscape Design' },
  { id: 'landscaping_lawn_care', label: 'Landscaping / Lawn Care' },
  { id: 'marketing_web_design', label: 'Marketing / Web Design' },
  { id: 'martial_arts', label: 'Martial Arts' },
  { id: 'masonry_chimney', label: 'Masonry / Chimney' },
  { id: 'meal_prep_delivery', label: 'Meal Prep / Delivery' },
  { id: 'mold_remediation', label: 'Mold Remediation' },
  { id: 'mortgage_lending', label: 'Mortgage / Lending' },
  { id: 'motorcycle_repair', label: 'Motorcycle Repair' },
  { id: 'moving_services', label: 'Moving Services' },
  { id: 'outdoor_holiday_lighting', label: 'Outdoor & Holiday Lighting' },
  { id: 'painting', label: 'Painting' },
  { id: 'personal_trainer_gym', label: 'Personal Trainer / Gym' },
  { id: 'pest_control', label: 'Pest Control' },
  { id: 'pet_grooming', label: 'Pet Grooming' },
  { id: 'pet_training', label: 'Pet Training' },
  { id: 'photography', label: 'Photography' },
  { id: 'pizza_restaurant', label: 'Pizza Restaurant' },
  { id: 'plumbing', label: 'Plumbing' },
  { id: 'pool_installation', label: 'Pool Installation' },
  { id: 'pool_service', label: 'Pool Service' },
  { id: 'pressure_washing', label: 'Pressure Washing' },
  { id: 'printing_signs', label: 'Printing / Signs' },
  { id: 'property_management', label: 'Property Management' },
  { id: 'real_estate_investing_cash_buyer', label: 'Real Estate Investing / Cash Buyer' },
  { id: 'realtor', label: 'Realtor' },
  { id: 'restaurant', label: 'Restaurant' },
  { id: 'roofing', label: 'Roofing' },
  { id: 'sandwich_shop_deli', label: 'Sandwich Shop / Deli' },
  { id: 'senior_care_aging_in_place', label: 'Senior Care / Aging in Place' },
  { id: 'siding_installation', label: 'Siding Installation' },
  { id: 'small_engine_repair', label: 'Small Engine Repair' },
  { id: 'snow_removal_plowing', label: 'Snow Removal / Plowing' },
  { id: 'solar_installation', label: 'Solar Installation' },
  { id: 'storage_containers', label: 'Storage / Containers' },
  { id: 'tax_cpa_bookkeeping', label: 'Tax / CPA / Bookkeeping' },
  { id: 'tires_wheels', label: 'Tires & Wheels' },
  { id: 'title_closing_services', label: 'Title / Closing Services' },
  { id: 'tree_service_arborist', label: 'Tree Service / Arborist' },
  { id: 'veterinary', label: 'Veterinary' },
  { id: 'videography', label: 'Videography' },
  { id: 'water_filtration_softeners', label: 'Water Filtration / Softeners' },
  { id: 'window_cleaning', label: 'Window Cleaning' },
  { id: 'window_replacement', label: 'Window Replacement' },
  { id: 'window_tinting', label: 'Window Tinting' },
  { id: 'window_treatments_blinds', label: 'Window Treatments / Blinds' },
  { id: 'wine_spirits', label: 'Wine & Spirits' },
  { id: 'yoga_pilates_fitness_studio', label: 'Yoga / Pilates / Fitness Studio' },
  { id: 'other_not_listed', label: 'Other / Not listed' },
];

/* ---- curated Tier-1 customer-source integrations (native connectors are the
   committed set; universal options mean the owner is never blocked). ---- */
type NativeConnector = { id: string; name: string; blurb: string; logo: VendorId };
const NATIVE_CONNECTORS: NativeConnector[] = [
  { id: 'jobber', name: 'Jobber', blurb: 'Field-service jobs & customers', logo: 'jobber' },
  { id: 'housecall', name: 'Housecall Pro', blurb: 'Home-service scheduling & invoicing', logo: 'housecall' },
  { id: 'square', name: 'Square', blurb: 'Payments, POS & customer directory', logo: 'square' },
  { id: 'quickbooks', name: 'QuickBooks', blurb: 'Invoices & customer records', logo: 'quickbooks' },
];

/* The REAL per-tenant embed snippet (M8 fix), built from the client's own
   widget embed_key so a client never copies someone else's snippet. */
const WIDGET_ORIGIN = (API_BASE || window.location.origin).replace(/\/+$/, '');
function embedSnippet(embedKey: string): string {
  return (
    `<div class="rhody-reviews" data-key="${embedKey}"></div>\n` +
    `<script src="${WIDGET_ORIGIN}/w/widget.js" async></script>`
  );
}

/* Brand-voice preview copy, keyed by tone. */
const VOICE_PREVIEW: Record<string, string> = {
  'Warm & personal':
    '"Thanks so much, James! Means the world to our family-owned crew - glad we got your hot water back same day. We\'re here 24/7 whenever you need us. - Mike, Owner"',
  Professional:
    '"Thank you for the kind words, James. We\'re glad we could resolve your water heater promptly. We appreciate your business. - Mike, Owner"',
  Playful:
    '"James, you legend! Hot showers restored, crisis averted. Call us anytime - we never sleep (literally, 24/7). - Mike, Owner"',
};
const TONES = Object.keys(VOICE_PREVIEW);

/* ===================================================================
   Item-0 20-second diagnostic (Phase 4, spec §6 Item 0). Two tappable
   questions that drive import pre-routing (Item 2) + brand-voice defaults
   (Item 3). Optional + non-blocking -- NEVER a required gate.
   =================================================================== */

/* Q(a): what the owner uses to schedule/invoice. The first four ids match
   NATIVE_CONNECTORS exactly, so a named tool pre-selects its one-tap card. */
type ScheduleTool = { id: string; label: string; logo?: VendorId };
const SCHEDULE_TOOLS: ScheduleTool[] = [
  { id: 'jobber', label: 'Jobber', logo: 'jobber' },
  { id: 'housecall', label: 'Housecall Pro', logo: 'housecall' },
  { id: 'square', label: 'Square', logo: 'square' },
  { id: 'quickbooks', label: 'QuickBooks', logo: 'quickbooks' },
  { id: 'spreadsheet', label: 'A spreadsheet' },
  { id: 'none', label: 'None of these' },
];
const NATIVE_TOOL_IDS = new Set(['jobber', 'housecall', 'square', 'quickbooks']);

/* Q(b): rough count of past customers the owner could ask for a review. */
type CustomerEstimate = { id: string; label: string };
const CUSTOMER_ESTIMATES: CustomerEstimate[] = [
  { id: 'under_25', label: 'Fewer than 25' },
  { id: '25_100', label: '25 to 100' },
  { id: '100_500', label: '100 to 500' },
  { id: '500_plus', label: '500 or more' },
];

/* ---- Per-industry brand-voice defaults (Item 3). Derived from the chosen
   category so the voice fields are editable-not-blank. Categories are grouped
   into a few archetypes; a generic fallback covers anything unmapped. tone MUST
   be one of TONES. `alwaysMention` seeds the "always mention" field. ---- */
type VoiceDefault = { tone: string; alwaysMention: string };
const VOICE_ARCHETYPES: Record<string, VoiceDefault> = {
  trades: { tone: 'Warm & personal', alwaysMention: 'licensed and insured, on-time, workmanship guaranteed' },
  food: { tone: 'Playful', alwaysMention: 'made fresh, family-owned, friendly service' },
  professional: { tone: 'Professional', alwaysMention: 'responsive, detail-oriented, trusted local advice' },
  wellness: { tone: 'Warm & personal', alwaysMention: 'welcoming, personalized, results you can feel' },
  auto: { tone: 'Professional', alwaysMention: 'honest pricing, quality work, done right the first time' },
  retail: { tone: 'Warm & personal', alwaysMention: 'quality selection, helpful staff, locally owned' },
  pets: { tone: 'Warm & personal', alwaysMention: 'gentle, caring, treat every pet like our own' },
};
/* Map a category id (or its keyword shape) onto an archetype. */
function archetypeFor(categoryId: string): keyof typeof VOICE_ARCHETYPES {
  const c = categoryId;
  if (/restaurant|bakery|cafe|catering|pizza|deli|sandwich|breakfast|meal|farm_stand|wine|grocer/.test(c)) return 'food';
  if (/attorney|accountant|tax|cpa|bookkeep|financial|insurance|mortgage|realtor|real_estate|appraiser|title|property_management|marketing|inspection/.test(c)) return 'professional';
  if (/yoga|pilates|fitness|gym|personal_trainer|martial_arts|salon|spa|photography|videography|interior_design/.test(c)) return 'wellness';
  if (/auto|tire|motorcycle|boat|small_engine|detailing|collision|body/.test(c)) return 'auto';
  if (/vet|pet|dog|grooming/.test(c)) return 'pets';
  if (/furniture_store|garden_center|nursery|florist|printing|signs|storage/.test(c)) return 'retail';
  // Default: home-service trades (the ICP) -- landscaping, plumbing, HVAC, etc.
  return 'trades';
}
/* Compute the seed defaults for the voice fields from category + business name.
   Sign-off falls back to a business-name team line so it is never blank. */
function voiceDefaultsFor(
  categoryId: string,
  businessName: string,
): { tone: string; signoff: string; alwaysMention: string } {
  const a = VOICE_ARCHETYPES[archetypeFor(categoryId)];
  const name = businessName.trim();
  return {
    tone: a.tone,
    signoff: name ? `- The ${name} Team` : '- The Team',
    alwaysMention: a.alwaysMention,
  };
}

/* ---- small inline icon helpers ---- */
function Arrow() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function BackArrow() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M19 12H5M11 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSession();
  const integrationRequest = useIntegrationRequest();
  const { data: me } = useMe();
  const { importRows } = useCustomerMutations();
  const connectors = useConnectors();
  const customers = useCustomers();
  const customerCount = customers.data?.count ?? 0;
  const widgetQ = useWidget();
  const embedKey = widgetQ.data?.widget.embed_key ?? null;
  const embedCode = embedKey
    ? embedSnippet(embedKey)
    : '<!-- Your widget snippet appears here once your account finishes setting up. -->';

  // CANONICAL Google connection state -- the single source of truth for the
  // "Connected" render AND the green-light send gate (Phase-1, UNCHANGED).
  const business = useBusiness();
  const googleStatus =
    business.data?.google_connection?.google_connection_status ??
    me?.google_connection_status ??
    'not_started';
  const googleConnected = googleStatus === 'connected';
  const googleTokenValid =
    business.data?.google_connection?.oauth_token_valid ??
    me?.oauth_token_valid ??
    false;

  const updateBusiness = useUpdateBusiness();
  const progress = useSetupProgress();
  // FIRST-ASK now lives on the single Requests page: the onboarding "Send
  // requests" CTA NAVIGATES there (the card-at-send inline step + composer all
  // live on that one screen now -- the old popup is fully retired).

  // activeTask === null -> the checklist home; a task id -> that task's flow.
  const [activeTask, setActiveTask] = useState<OnboardingTaskId | null>(null);
  const [tone, setTone] = useState(TONES[0]);
  const [copied, setCopied] = useState(false);

  // ---- brand-voice (controlled, persisted to /business) ----
  const [signoff, setSignoff] = useState('');
  const [alwaysMention, setAlwaysMention] = useState('');
  const [voiceError, setVoiceError] = useState<string | null>(null);

  // ---- business category (no category is gated) ----
  const [category, setCategory] = useState('');

  // ---- Item-0 diagnostic answers (Phase 4). Local source of truth; drives
  // import pre-routing + brand-voice defaults. Persisted locally + best-effort
  // to the server brand_voice blob. ----
  const [scheduleTool, setScheduleTool] = useState('');
  const [pastCustomerEstimate, setPastCustomerEstimate] = useState('');
  // Seed the brand-voice fields from the industry defaults at most once per
  // open of the voice task (only when nothing is set yet -- never clobbers).
  const voiceSeededRef = useRef(false);

  // ---- customers task state ----
  const [addOpen, setAddOpen] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [comingSoon, setComingSoon] = useState<string | null>(null);

  // ---- integration grid state ----
  const [intSearch, setIntSearch] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestName, setSuggestName] = useState('');
  const [suggestCategory, setSuggestCategory] = useState('');
  const [suggestNote, setSuggestNote] = useState('');
  const [suggested, setSuggested] = useState(false);

  // ---- persistence (hydrate once per tenant) ----
  const clientId = me?.client_id;
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !clientId) return;
    hydratedRef.current = true;
    const saved = loadOnboarding(clientId);
    if (saved) {
      if (typeof saved.category === 'string') setCategory(saved.category);
      if (typeof saved.tone === 'string') setTone(saved.tone);
      if (typeof saved.signoff === 'string') setSignoff(saved.signoff);
      if (typeof saved.alwaysMention === 'string') setAlwaysMention(saved.alwaysMention);
      if (typeof saved.scheduleTool === 'string') setScheduleTool(saved.scheduleTool);
      if (typeof saved.pastCustomerEstimate === 'string')
        setPastCustomerEstimate(saved.pastCustomerEstimate);
    }
    // A ?task=<id> (or legacy ?step=N, e.g. the Google-OAuth return landing on
    // /onboarding?task=connect) opens that task; else the saved task; else home.
    const taskParam = searchParams.get('task');
    const stepParam = parseInt(searchParams.get('step') ?? '', 10);
    let toOpen: OnboardingTaskId | null = null;
    if (taskParam && (VALID_TASKS as string[]).includes(taskParam)) {
      toOpen = taskParam as OnboardingTaskId;
    } else if (Number.isFinite(stepParam) && LEGACY_STEP_TO_TASK[stepParam]) {
      toOpen = LEGACY_STEP_TO_TASK[stepParam];
    } else if (saved?.activeTask && (VALID_TASKS as string[]).includes(saved.activeTask)) {
      toOpen = saved.activeTask;
    }
    if (toOpen) {
      setActiveTask(toOpen);
      window.scrollTo(0, 0);
    }
  }, [clientId, searchParams]);

  // DEV-ONLY visual-walkthrough backstop (Brad, 2026-07-08): the hydration
  // effect above only honors ?task= once a client_id exists (a real session).
  // In the local dev preview there is no session, so deep-linking a task panel
  // for a screenshot would otherwise stay on the checklist home. This run-once,
  // DEV-gated effect opens the requested task from ?task= even without a
  // client_id. It is statically dropped from prod builds (import.meta.env.DEV),
  // so live routing/gating is completely unchanged; run-once (ref) so tapping
  // "Back to checklist" is not re-hijacked by the still-present URL param.
  const devTaskHandledRef = useRef(false);
  useEffect(() => {
    if (!import.meta.env.DEV || devTaskHandledRef.current) return;
    const taskParam = searchParams.get('task');
    if (taskParam && (VALID_TASKS as string[]).includes(taskParam)) {
      devTaskHandledRef.current = true;
      setActiveTask(taskParam as OnboardingTaskId);
      window.scrollTo(0, 0);
    }
  }, [searchParams]);

  // Persist the open task + durable inputs. Once the account is CORE-COMPLETE
  // (the activation path is done), clear the key so a later Google-connect from
  // Settings is not bounced back into onboarding.
  useEffect(() => {
    if (!clientId || !hydratedRef.current) return;
    if (progress.coreComplete) {
      clearOnboardingForTenant(clientId);
      return;
    }
    saveOnboarding(clientId, {
      activeTask, category, tone, signoff, alwaysMention,
      scheduleTool, pastCustomerEstimate,
    });
    // Deps below are a CONSTANT-LENGTH literal (9 primitives, no spread/
    // conditional), so React's deps array never changes size between renders.
  }, [
    clientId, progress.coreComplete, activeTask, category, tone, signoff,
    alwaysMention, scheduleTool, pastCustomerEstimate,
  ]);

  // ---- METRICS (spec §11): emit funnel milestones as DERIVED state proves
  // them -- each checklist item completed, the ACTIVATION event (Google
  // canonically connected + first request sent), and time-to-value. Each fires
  // ONCE per tenant (trackMilestoneOnce de-dupes in storage), so mounts /
  // reloads never double-count. NO PII: only the tenant's client_id (a UUID),
  // item ids, counts, and timestamps leave the app.
  //
  // deps = [clientId, progress.loading, doneSignature] -- CONSTANT-LENGTH (3
  // primitives). doneSignature is a fixed-shape string ("101...") of the
  // per-item booleans, so watching N items complete NEVER grows the deps array
  // (which would trip "final argument passed to useEffect changed size between
  // renders"). Reasoned render paths: the effect runs after every hook above
  // it, before the `if (activeTask) return renderTask(...)` early-return, so its
  // hook order is stable on every render; only doneSignature's VALUE changes as
  // items flip done, never the array's length.
  const doneSignature = progress.steps.map((s) => (s.done ? '1' : '0')).join('');
  useEffect(() => {
    if (!clientId || progress.loading) return;
    const doneById: Record<string, boolean> = {};
    for (const s of progress.steps) doneById[s.id] = s.done;

    // Each checklist item completed. Widget has no honest "installed" signal
    // (never fabricated done), so it is intentionally omitted.
    (['gbp', 'import', 'voice', 'reactivation', 'requests'] as const).forEach((id) => {
      if (doneById[id]) {
        trackMilestoneOnce(clientId, `item_completed:${id}`, { item: id });
      }
    });

    // ACTIVATION (spec §1/§11): Google canonically connected AND first request
    // sent. On the activation edge only, also emit time-to-value vs first-seen.
    if (doneById.gbp && doneById.requests) {
      const firstActivation = trackMilestoneOnce(clientId, 'activation', {
        via: 'google_connected+first_request_sent',
      });
      if (firstActivation) {
        const firstSeen = getFirstSeen();
        const now = Date.now();
        track('time_to_value', {
          client_id: clientId,
          first_seen_ts: firstSeen,
          activated_at: now,
          ttv_ms: firstSeen != null ? now - firstSeen : null,
        });
      }
    }
    // progress.steps is read via doneSignature (a scalar) which keys the rerun;
    // listing it directly is unnecessary and would not change size anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, progress.loading, doneSignature]);

  // Item-3 brand-voice DEFAULTS: when the voice task opens, pre-fill tone /
  // sign-off / "always mention" with sensible per-industry defaults (derived
  // from the diagnostic's category) so the fields are editable-not-blank. Only
  // seeds when the owner has typed nothing AND the server has no saved voice --
  // it never clobbers a real value. Runs at most once per open of the task.
  const serverBrandVoice = business.data?.brand_voice;
  useEffect(() => {
    if (activeTask !== 'voice') {
      voiceSeededRef.current = false;
      return;
    }
    if (voiceSeededRef.current) return;
    // Wait until the profile has loaded so we don't seed over a saved voice.
    if (business.isLoading) return;
    voiceSeededRef.current = true;
    const serverHasVoice = Boolean(
      serverBrandVoice &&
        (String(serverBrandVoice.signature ?? '').trim() ||
          String(serverBrandVoice.sample_phrases ?? '').trim() ||
          String(serverBrandVoice.tone ?? '').trim()),
    );
    if (serverHasVoice) return;
    const d = voiceDefaultsFor(category, me?.business_name ?? '');
    // Only fill blanks -- respect anything the owner already typed this session.
    setTone((t) => (t && t !== TONES[0] ? t : d.tone));
    setSignoff((s) => (s.trim() ? s : d.signoff));
    setAlwaysMention((m) => (m.trim() ? m : d.alwaysMention));
  }, [activeTask, business.isLoading, serverBrandVoice, category, me?.business_name]);

  const q = intSearch.trim().toLowerCase();
  const liveConns = connectors.data?.connectors ?? [];
  const wiredProviders = new Set(
    liveConns
      .filter((x) => x.connected || (x.configured && !!x.connect_url))
      .map((x) => x.provider),
  );
  const nativeMatches = NATIVE_CONNECTORS.filter(
    (c) =>
      wiredProviders.has(c.id) &&
      (!q || c.name.toLowerCase().includes(q) || c.blurb.toLowerCase().includes(q)),
  );
  // Sources that are actually CONNECTED (not just configured) -> surface the
  // one-line plain-English trigger-arm confirm (forward-trigger scaffold). The
  // component no-ops for any source it has no preset for.
  const connectedSources = liveConns
    .filter((x) => x.connected)
    .map((x) => x.provider);

  // ---- Item-2 import PRE-ROUTING (Phase 4). The Item-0 diagnostic's schedule
  // tool picks the recommended path shown FIRST in the Customers task. Never a
  // dead end -- Zapier / CSV / manual always still render below. ----
  const routedNative = NATIVE_TOOL_IDS.has(scheduleTool)
    ? NATIVE_CONNECTORS.find((c) => c.id === scheduleTool) ?? null
    : null;
  const importRecoKind: 'native' | 'csv' | 'manual' | null = routedNative
    ? 'native'
    : scheduleTool === 'spreadsheet'
      ? 'csv'
      : scheduleTool === 'none'
        ? 'manual'
        : null;

  // ---- navigation between the home + a task ----
  function openTask(task: OnboardingTaskId) {
    setActiveTask(task);
    window.scrollTo(0, 0);
  }
  function backToHome() {
    setActiveTask(null);
    window.scrollTo(0, 0);
  }

  // Persist the brand voice to /business (brand_voice blob). MERGE with the
  // current blob so a voice save never wipes the other config that lives in the
  // same JSON blob (the Item-0 diagnostic answers, the monthly goal, booking).
  async function saveBrandVoice() {
    setVoiceError(null);
    try {
      const currentBv = business.data?.brand_voice ?? {};
      await updateBusiness.save({
        brand_voice: {
          ...currentBv,
          tone: tone.trim(),
          signature: signoff.trim(),
          sample_phrases: alwaysMention.trim(),
        },
      });
    } catch {
      setVoiceError(
        'We could not save your brand voice just now. You can set it anytime in Settings -> Brand voice.',
      );
    }
  }
  function saveVoiceAndBack() {
    void saveBrandVoice();
    backToHome();
  }

  // Persist the Item-0 diagnostic. The LOCAL copy (onboardingState, written by
  // the persist effect) is the source of truth for pre-routing + defaults; this
  // additionally mirrors the two answers into the server brand_voice blob (no
  // migration -- see engine/api/v1/account.py). Best-effort: a failure leaves
  // the local copy intact, so pre-routing still works. Merges so it never wipes
  // an existing voice.
  async function saveDiagnostic() {
    try {
      const currentBv = business.data?.brand_voice ?? {};
      // ASSUME the brand voice for them (spec: "assume their brand voice for
      // them"): if the account has no voice yet, seed the per-industry default
      // from the diagnostic so replies are editable-not-blank and the owner
      // never has to open a brand-voice step. Never clobbers an existing voice.
      const hasVoice = Boolean(
        String(currentBv.tone ?? '').trim() ||
          String(currentBv.signature ?? '').trim() ||
          String(currentBv.sample_phrases ?? '').trim(),
      );
      const voiceSeed = hasVoice
        ? {}
        : (() => {
            const d = voiceDefaultsFor(category, me?.business_name ?? '');
            return {
              tone: d.tone,
              signature: d.signoff,
              sample_phrases: d.alwaysMention,
            };
          })();
      await updateBusiness.save({
        brand_voice: {
          ...currentBv,
          ...voiceSeed,
          schedule_tool: scheduleTool,
          past_customer_estimate: pastCustomerEstimate,
        },
      });
    } catch {
      /* server mirror is best-effort; the local copy still drives everything */
    }
  }
  function saveDiagnosticAndBack() {
    void saveDiagnostic();
    backToHome();
  }

  function copyEmbed() {
    navigator.clipboard?.writeText(embedCode).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => {
        /* clipboard unavailable; leave state unchanged */
      },
    );
  }

  // ---- real external actions ----
  function handleConnectGoogle() {
    if (!clientId) {
      setComingSoon('Still finishing sign-in. Try again in a moment.');
      return;
    }
    // Flush the open task NOW so the OAuth round-trip (which leaves the app and
    // returns via /integrations) resumes the Connect task with Google linked.
    saveOnboarding(clientId, {
      activeTask: 'connect', category, tone, signoff, alwaysMention,
      scheduleTool, pastCustomerEstimate,
    });
    void startGoogleConnect(clientId);
  }
  function handleConnectNative(id: string) {
    const live = (connectors.data?.connectors ?? []).find((x) => x.provider === id);
    if (live?.connect_url) {
      window.location.href = live.connect_url;
      return;
    }
    setComingSoon('For now, connect through Zapier, upload a CSV, or add customers manually.');
  }
  function handleConnectZapier() {
    setComingSoon(
      'Set up your Zapier / webhook key in Settings -> Integrations. ' +
        'It connects any tool today. For now you can also upload a CSV or add customers manually.',
    );
  }
  function handleUploadCsv() {
    setImportMsg(null);
    setComingSoon(null);
    fileInputRef.current?.click();
  }
  async function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportMsg('Reading file...');
    try {
      const text = await file.text();
      const parsed = parseCustomersCsv(text);
      if (parsed.rows.length === 0) {
        setImportMsg('No importable rows found. The CSV needs a name plus an email or phone column.');
        return;
      }
      const res = await importRows(parsed.rows);
      const dropped = parsed.totalRows - parsed.rows.length;
      const skipped = res.skipped_count + dropped;
      setImportMsg(
        `Imported ${res.imported} customer${res.imported === 1 ? '' : 's'}` +
          (skipped > 0
            ? `. Skipped ${skipped} row${skipped === 1 ? '' : 's'} (missing email/phone or invalid).`
            : '.'),
      );
    } catch (err) {
      setImportMsg(`Import failed${err instanceof Error ? ` (${err.message})` : ''}.`);
    }
  }
  function handleAddManually() {
    setComingSoon(null);
    setAddOpen(true);
  }
  // Download a ready-to-fill CSV template with the canonical columns
  // (name, phone, email, job_completed_date) + one example row.
  function downloadCsvTemplate() {
    const csv =
      'name,phone,email,job_completed_date\n' +
      'Sarah Lopez,(401) 555-0148,sarah@example.com,2026-06-14\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rhody-reviews-customers-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  function submitIntegrationRequest(req: { name: string; category: string; note: string }) {
    integrationRequest.mutate(
      {
        softwareName: req.name,
        businessCategory: req.category || undefined,
        note: req.note || undefined,
        submittedByEmail: user?.email ?? undefined,
      },
      { onSuccess: () => setSuggested(true) },
    );
  }
  function handleSendRequests() {
    // Go to the single Requests page to compose + send the first ask. The
    // card-at-send inline step (card-free owner's first real send) lives there
    // now, so onboarding no longer opens a popup or redirects to /choose-plan.
    navigate('/requests');
  }

  // GREEN-LIGHT SEND GATE (spec §2/§5, UNCHANGED): the first real request may
  // only be sent when Google is CANONICALLY connected AND >=1 customer exists.
  const sendReady = googleConnected && customerCount > 0;

  /* ---- the task shell (a launched flow) ---- */
  const taskFoot = (label: string, onPrimary: () => void) => (
    <div className="obfoot">
      <button className="btn btn--ghost" onClick={backToHome}>
        Back to checklist
      </button>
      <button className="btn btn--primary" onClick={onPrimary}>
        {label} <Arrow />
      </button>
    </div>
  );

  function renderTask(task: OnboardingTaskId) {
    return (
      <div className="ob">
        <header className="obtop">
          <BrandMark size={30} />
          <button className="skip" onClick={backToHome}>
            Back to setup
          </button>
        </header>

        <button className="ob-back" onClick={backToHome}>
          <BackArrow /> Setup checklist
        </button>

        <div className="panel">
          {task === 'diagnostic' && (
            <>
              <div className="ey">Quick start &middot; 20 seconds</div>
              <h2>Two quick questions</h2>
              <p className="desc">
                This tells Rhody Reviews how you run the business so it sets up the rest the
                right way. Optional, and you can change any of it later.
              </p>

              <div className="diag">
                <div className="diag__q">
                  <span className="diag__qlab">What do you use to schedule or invoice?</span>
                  <div className="diag__chips">
                    {SCHEDULE_TOOLS.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={'diag-chip' + (scheduleTool === t.id ? ' diag-chip--sel' : '')}
                        aria-pressed={scheduleTool === t.id}
                        onClick={() =>
                          setScheduleTool((cur) => (cur === t.id ? '' : t.id))
                        }
                      >
                        {t.logo && <VendorLogo id={t.logo} size={20} />}
                        <span>{t.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="diag__q">
                  <span className="diag__qlab">
                    Roughly how many past customers could you ask for a review?
                  </span>
                  <div className="diag__chips">
                    {CUSTOMER_ESTIMATES.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className={
                          'diag-chip' + (pastCustomerEstimate === c.id ? ' diag-chip--sel' : '')
                        }
                        aria-pressed={pastCustomerEstimate === c.id}
                        onClick={() =>
                          setPastCustomerEstimate((cur) => (cur === c.id ? '' : c.id))
                        }
                      >
                        <span>{c.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="diag__q">
                  <span className="diag__qlab">
                    What kind of business is this? <span className="diag__opt">(optional)</span>
                  </span>
                  <select
                    className="sel"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="">Select your category</option>
                    {BUSINESS_CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <span className="diag__hint">
                    Tailors the wording of your requests and replies to your line of work.
                  </span>
                </div>
              </div>

              {taskFoot('Save & done', saveDiagnosticAndBack)}
            </>
          )}

          {task === 'industry' && (
            <>
              <div className="ey">Quick start &middot; Industry</div>
              <h2>What kind of business is this?</h2>
              <p className="desc">
                This helps Rhody Reviews tailor your review requests and replies to your
                line of work. Optional, and you can change it anytime.
              </p>
              <div className="formgrid2">
                <label className="fld full">
                  <span className="lab">Business category</span>
                  <select
                    className="sel"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="">Select your category</option>
                    {BUSINESS_CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {taskFoot('Done', backToHome)}
            </>
          )}

          {task === 'connect' && (
            <>
              <div className="ey">Step 1 &middot; Google Business Profile</div>
              <h2>Connect your Google Business Profile</h2>
              <p className="desc">
                This is your Google Business listing, the one customers leave reviews on, not your
                login. We pull in your existing reviews and start monitoring for new ones in real
                time, so Rhody can ask, reply, and report for you.
              </p>
              <div className="gconnect">
                <GoogleG size={38} />
                <div className="t">
                  <b>Google Business Profile</b>
                  <span>Reviews, ratings &amp; insights</span>
                </div>
                {googleConnected ? (
                  <span className="chip green" style={{ whiteSpace: 'nowrap' }}>
                    Connected
                  </span>
                ) : googleTokenValid ? (
                  <span
                    className="chip"
                    style={{ whiteSpace: 'nowrap', background: 'var(--surface-2)', color: 'var(--muted-2)' }}
                  >
                    Confirm below
                  </span>
                ) : (
                  <button className="btn btn--primary" onClick={handleConnectGoogle}>
                    Connect
                  </button>
                )}
              </div>
              <p className="desc" style={{ fontSize: 13, marginTop: 14 }}>
                {googleConnected
                  ? 'Google is connected and your business is confirmed. Your review link is ready.'
                  : googleTokenValid
                    ? 'Google is linked. Confirm your business below so your review link is right before any request goes out.'
                    : "You'll be sent to Google to grant access, then brought back here. We pull in your real reviews once you connect. Not ready? You can also link your business by name below."}
              </p>

              {/* CONFIRM-YOUR-BUSINESS validation gate (Phase-1, UNCHANGED). */}
              <ConfirmBusinessCard />

              {taskFoot('Done', backToHome)}
            </>
          )}

          {task === 'customers' && (
            <>
              <div className="ey">Step 2 &middot; Customers</div>
              <h2>Bring in your customers</h2>
              <p className="desc">
                Rhody Reviews asks every past and future customer for a review -
                automatically. Connect the software you already use, or bring a list your
                way. You are never locked in.
              </p>

              {/* Trigger-arm confirm (forward-trigger scaffold): when a tool is
                  connected, surface ONE plain-English confirm for the automatic
                  review trigger. No-ops for sources without a preset. */}
              {connectedSources.map((src) => (
                <TriggerArmConfirm key={src} source={src} clientId={clientId} />
              ))}

              {/* Item-2 PRE-ROUTING (Phase 4): lead with the path implied by the
                  Item-0 diagnostic. Never a dead end -- every option still shows
                  below. */}
              {importRecoKind && (
                <div className="int-reco">
                  <div className="int-reco__lab">Based on your setup, start here</div>
                  {importRecoKind === 'native' && routedNative && (
                    <button
                      className="itile itile--reco"
                      onClick={() => handleConnectNative(routedNative.id)}
                    >
                      <VendorLogo id={routedNative.logo} size={40} />
                      <span className="t">
                        <b>Connect {routedNative.name}</b>
                        <span>{routedNative.blurb}</span>
                      </span>
                      <span className="int-reco__go" aria-hidden="true">
                        <Arrow />
                      </span>
                    </button>
                  )}
                  {importRecoKind === 'csv' && (
                    <button className="itile itile--reco" onClick={handleUploadCsv}>
                      <span className="lg" style={{ background: 'var(--surface-2)', color: 'var(--accent-soft)' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 20, height: 20 }}>
                          <path d="M12 3v12M7 10l5 5 5-5M5 21h14" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span className="t">
                        <b>Upload your spreadsheet</b>
                        <span>CSV: name, phone, email, job date</span>
                      </span>
                      <span className="int-reco__go" aria-hidden="true">
                        <Arrow />
                      </span>
                    </button>
                  )}
                  {importRecoKind === 'manual' && (
                    <button className="itile itile--reco" onClick={handleAddManually}>
                      <span className="lg" style={{ background: 'var(--surface-2)', color: 'var(--accent-soft)' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 20, height: 20 }}>
                          <circle cx="12" cy="8" r="3.2" />
                          <path d="M5 20a7 7 0 0114 0" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span className="t">
                        <b>Add your first customers</b>
                        <span>Type in a few to start - no tool needed</span>
                      </span>
                      <span className="int-reco__go" aria-hidden="true">
                        <Arrow />
                      </span>
                    </button>
                  )}
                  <div className="int-reco__alt">
                    Prefer another way? Every option is below.
                  </div>
                </div>
              )}

              <label className="intsearch">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4-4" strokeLinecap="round" />
                </svg>
                <input
                  placeholder="Search software (Jobber, Square, QuickBooks...)"
                  value={intSearch}
                  onChange={(e) => setIntSearch(e.target.value)}
                />
              </label>

              {nativeMatches.length > 0 ? (
                <>
                  <div className="intgroup-label first">Direct connections</div>
                  <div className="intgrid">
                    {nativeMatches.map((c) => (
                      <button key={c.id} className="itile" onClick={() => handleConnectNative(c.id)}>
                        <VendorLogo id={c.logo} size={40} />
                        <span className="t">
                          <b>{c.name}</b>
                          <span>{c.blurb}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              ) : q ? (
                <>
                  <div className="intgroup-label first">Direct connections</div>
                  <div className="intempty">
                    No direct connection for that yet - use Zapier, a CSV, or suggest it below.
                  </div>
                </>
              ) : null}

              <div className="intgroup-label">Any tool, or no tool</div>
              <div className="intgrid universal">
                <button className="itile uni" onClick={handleConnectZapier}>
                  <VendorLogo id="zapier" size={40} />
                  <span className="t">
                    <b>Connect via Zapier</b>
                    <span>6,000+ apps</span>
                  </span>
                </button>
                <button className="itile uni" onClick={handleUploadCsv}>
                  <span className="lg" style={{ background: 'var(--surface-2)', color: 'var(--accent-soft)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 20, height: 20 }}>
                      <path d="M12 3v12M7 10l5 5 5-5M5 21h14" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="t">
                    <b>Upload CSV</b>
                    <span>name, phone, email, job date</span>
                  </span>
                </button>
                <button className="itile uni" onClick={handleAddManually}>
                  <span className="lg" style={{ background: 'var(--surface-2)', color: 'var(--accent-soft)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 20, height: 20 }}>
                      <circle cx="12" cy="8" r="3.2" />
                      <path d="M5 20a7 7 0 0114 0" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span className="t">
                    <b>Add manually</b>
                    <span>Type in a few to start</span>
                  </span>
                </button>
              </div>

              <button className="csv-tmpl" onClick={downloadCsvTemplate}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M12 3v12M7 10l5 5 5-5M5 21h14" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Download CSV template (name, phone, email, job date)
              </button>

              {importMsg && (
                <div className="muted" style={{ fontSize: 13, margin: '14px 0 0', padding: '8px 12px', background: 'var(--surface-2, rgba(0,0,0,.03))', borderRadius: 8 }}>
                  {importMsg}
                </div>
              )}
              {comingSoon && (
                <div className="muted" style={{ fontSize: 13, margin: '14px 0 0', padding: '8px 12px', background: 'var(--surface-2, rgba(0,0,0,.03))', borderRadius: 8 }}>
                  {comingSoon}
                </div>
              )}

              {suggested ? (
                <div className="suggest-confirm">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
                    <path d="M5 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Thanks - we&apos;ll add it. We track every suggestion and prioritize the
                  most-requested tools.
                </div>
              ) : !suggestOpen ? (
                <button className="suggest-link" onClick={() => setSuggestOpen(true)}>
                  Don&apos;t see your software? Suggest it
                </button>
              ) : (
                <div className="suggest-form">
                  <div className="formgrid2">
                    <label className="fld">
                      <span className="lab">Software name *</span>
                      <input className="inp" placeholder="e.g. ServiceTitan" value={suggestName} onChange={(e) => setSuggestName(e.target.value)} />
                    </label>
                    <label className="fld">
                      <span className="lab">Business category</span>
                      <select className="sel" value={suggestCategory} onChange={(e) => setSuggestCategory(e.target.value)}>
                        <option value="">Select a category</option>
                        <option>Landscaping / lawn care</option>
                        <option>Plumbing</option>
                        <option>HVAC</option>
                        <option>Electrical</option>
                        <option>Cleaning</option>
                        <option>Roofing</option>
                        <option>Pest control</option>
                        <option>Auto / repair</option>
                        <option>Home services - other</option>
                        <option>Other</option>
                      </select>
                    </label>
                    <label className="fld full">
                      <span className="lab">Note (optional)</span>
                      <input className="inp" placeholder="Anything that helps us prioritize" value={suggestNote} onChange={(e) => setSuggestNote(e.target.value)} />
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <button
                      className="btn btn--primary"
                      disabled={!suggestName.trim() || integrationRequest.isPending}
                      style={!suggestName.trim() || integrationRequest.isPending ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                      onClick={() => submitIntegrationRequest({ name: suggestName.trim(), category: suggestCategory, note: suggestNote.trim() })}
                    >
                      {integrationRequest.isPending ? 'Sending...' : 'Submit suggestion'}
                    </button>
                    <button className="btn btn--ghost" onClick={() => setSuggestOpen(false)}>
                      Cancel
                    </button>
                  </div>
                  {integrationRequest.isError && (
                    <div className="suggest-error">
                      Sorry, that did not go through. Please try again in a moment.
                    </div>
                  )}
                </div>
              )}

              {taskFoot('Done', backToHome)}
            </>
          )}

          {task === 'reactivation' && (
            <>
              <ReactivationStep
                onEnrolled={(n) => {
                  // Record the SERVER-CONFIRMED enrollment so the checklist's
                  // Reactivation item derives done (the one item with no server
                  // read). Fires only on a real enroll success (n >= 1).
                  if (n >= 1) setReactivationEnrolled(clientId, n);
                }}
              />
              {taskFoot('Done', backToHome)}
            </>
          )}

          {task === 'voice' && (
            <>
              <div className="ey">Step 3 &middot; Brand voice</div>
              <h2>Teach Rhody Reviews your voice</h2>
              <p className="desc">
                Every AI-drafted reply sounds like you - never robotic. You approve before
                anything posts.
              </p>
              <div className="formgrid2">
                <label className="fld">
                  <span className="lab">Tone</span>
                  <select className="sel" value={tone} onChange={(e) => setTone(e.target.value)}>
                    {TONES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fld">
                  <span className="lab">Sign-off</span>
                  <input className="inp" placeholder="e.g. - Mike, Owner" value={signoff} onChange={(e) => setSignoff(e.target.value)} />
                </label>
                <label className="fld full">
                  <span className="lab">Always mention</span>
                  <input className="inp" placeholder="e.g. family-owned, same-day service, 24/7 emergencies" value={alwaysMention} onChange={(e) => setAlwaysMention(e.target.value)} />
                </label>
              </div>
              {/* Convenient photo capture at signup (same control as Settings >
                  Business profile). Optional; setting it here is a shortcut, the
                  Settings home is always available if onboarding is skipped. */}
              <div style={{ marginTop: 18 }}>
                <ClientPhotoUpload variant="compact" />
              </div>
              <div className="replyprev">
                <div className="tag">AI reply preview &middot; 5-star review</div>
                {VOICE_PREVIEW[tone]}
              </div>
              {voiceError && (
                <div className="muted" style={{ fontSize: 13, margin: '14px 0 0', padding: '8px 12px', background: 'var(--surface-2, rgba(0,0,0,.03))', borderRadius: 8 }}>
                  {voiceError}
                </div>
              )}
              {taskFoot('Save & done', saveVoiceAndBack)}
            </>
          )}

          {task === 'widget' && (
            <>
              <div className="ey">Optional &middot; Widget</div>
              <h2>Show off your reviews</h2>
              <p className="desc">
                Drop your star rating and best reviews right on your website. Paste this once
                - it updates automatically. Nice to add once you have reviews to show.
              </p>
              <div className="code">{embedCode}</div>
              <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="btn btn--ghost" onClick={copyEmbed}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <rect x="9" y="9" width="11" height="11" rx="2" />
                    <path d="M5 15V5a2 2 0 012-2h8" strokeLinecap="round" />
                  </svg>
                  Copy code
                </button>
                {copied && <span className="copied-note">Copied to clipboard</span>}
              </div>
              {taskFoot('Done', backToHome)}
            </>
          )}

          {task === 'firstask' && (
            <>
              <div className="ey">Step 5 &middot; First ask</div>
              <h2>Send your first requests</h2>
              {customerCount > 0 ? (
                <p className="desc">
                  You have {customerCount} customer{customerCount === 1 ? '' : 's'} ready to be
                  asked. Send your first requests and watch the 5-stars roll in.
                </p>
              ) : (
                <p className="desc">
                  No customers yet. Import a list from the Customers step, then come back here
                  to send your first requests.
                </p>
              )}
              <div className="phone">
                <div className="meta">EXAMPLE MESSAGE</div>
                <div className="bubble">
                  Hi there - thanks for trusting us today! Quick favor: would you share a
                  5-star Google review? Takes 20 sec: rhody.to/r/example
                </div>
              </div>

              {/* GREEN-LIGHT SEND GATE (UNCHANGED): blocked until the review link
                  is validated (business confirmed) AND >=1 customer exists. */}
              {sendReady ? (
                <div className="send-gate send-gate--go">
                  <b style={{ color: 'var(--green)' }}>Ready to send.</b>{' '}
                  Sending to{' '}
                  <b style={{ color: 'var(--ink)' }}>
                    {customerCount} customer{customerCount === 1 ? '' : 's'}
                  </b>{' '}
                  by email. (Texting turns on once your carrier registration is approved.)
                </div>
              ) : (
                <div className="send-gate send-gate--block">
                  A couple of things first, so your requests land right:
                  <ul>
                    <li className={googleConnected ? 'ok' : undefined}>
                      {googleConnected
                        ? 'Google business confirmed'
                        : 'Finish connecting Google (Step 1) so your review link is valid'}
                    </li>
                    <li className={customerCount > 0 ? 'ok' : undefined}>
                      {customerCount > 0
                        ? `${customerCount} customer${customerCount === 1 ? '' : 's'} ready`
                        : 'Add at least one customer (Step 2)'}
                    </li>
                  </ul>
                </div>
              )}

              {/* The Send button is HARD-DISABLED until the gate is satisfied. */}
              <div className="obfoot">
                <button className="btn btn--ghost" onClick={backToHome}>
                  Back to checklist
                </button>
                <button
                  className="btn btn--primary"
                  onClick={handleSendRequests}
                  disabled={!sendReady}
                  style={!sendReady ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                >
                  Send requests <Arrow />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Hidden CSV picker (Customers task "Upload CSV"). */}
        <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onFileChosen} />

        {addOpen && <AddCustomerModal onSaved={() => { /* derived from customer_count */ }} onClose={() => setAddOpen(false)} />}
      </div>
    );
  }

  if (activeTask) return renderTask(activeTask);

  // ---- the checklist home (default) ----
  return (
    <OnboardingChecklist
      variant="full"
      onOpenTask={openTask}
      onGoToDashboard={() => navigate('/dashboard')}
      onSkipSetup={() => navigate('/dashboard')}
    />
  );
}
